// AdminTrialSignups — SUPERADMIN-only.
// Shows every Google trial signup (active / expired) and lets the superadmin
// revoke any of them.
import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card, Badge, Table, Toolbar, StatTile } from './_shared';
import api from '@/lib/api';
import { Sparkles, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminTrialSignups() {
  const [data, setData] = useState({ signups: [], total: 0, active: 0, expired: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/trial-signups'); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load trial signups'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (s) => {
    if (!window.confirm(`Revoke trial for ${s.email}? They will lose access immediately.`)) return;
    try { await api.delete(`/admin/trial-signups/${s.signup_id}`); toast.success('Trial revoked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const fmtCountdown = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'Expired';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m left`;
  };

  return (
    <AdminLayout title="Trial Signups" subtitle="Google · 24 hour">
      <Toolbar>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="w-3.5 h-3.5 text-amber-400"/> Capexara-managed view of every Gmail trial signup.
        </div>
      </Toolbar>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatTile label="Total signups" value={data.total} accent="slate"/>
        <StatTile label="Active trials" value={data.active} accent="emerald"/>
        <StatTile label="Expired"       value={data.expired} accent="red"/>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>
      ) : data.signups.length === 0 ? (
        <Card className="p-12 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-slate-500 mb-3"/>
          <h2 className="text-base text-slate-100 mb-1">No trial signups yet</h2>
          <p className="text-sm text-slate-400">When someone clicks "Start Free Trial" and signs in with Google, they'll show up here.</p>
        </Card>
      ) : (
        <Table
          getKey={(r) => r.signup_id}
          columns={[
            { key: 'name', header: 'Name', render: (r) => (
              <div className="flex items-center gap-2.5">
                {r.picture ? <img src={r.picture} alt="" className="w-7 h-7 rounded-full border border-slate-700"/> : <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700"/>}
                <div className="leading-tight">
                  <div className="text-slate-100" data-testid={`trial-name-${r.signup_id}`}>{r.name || r.email.split('@')[0]}</div>
                  <div className="text-[10px] tracking-wider text-slate-500 font-mono">{r.email}</div>
                </div>
              </div>
            )},
            { key: 'status', header: 'Status', render: (r) => r.status === 'EXPIRED'
                ? <Badge tone="red">EXPIRED</Badge>
                : <Badge tone="green">ACTIVE</Badge>
            },
            { key: 'remaining', header: 'Remaining', render: (r) => <span className="font-mono text-xs">{fmtCountdown(r.trialExpiresAt)}</span> },
            { key: 'started',  header: 'Started',   render: (r) => (r.trialStartedAt || '').slice(0,16).replace('T',' ') },
            { key: 'expires',  header: 'Expires',   render: (r) => (r.trialExpiresAt || '').slice(0,16).replace('T',' ') },
            { key: 'ip',       header: 'IP',        render: (r) => <span className="font-mono text-xs text-slate-400">{r.ip || '—'}</span> },
            { key: 'actions',  header: '',          render: (r) => (
              <div className="flex justify-end">
                <button onClick={() => revoke(r)} data-testid={`trial-revoke-${r.signup_id}`}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 transition">
                  <Trash2 className="w-3.5 h-3.5"/> Revoke
                </button>
              </div>
            )},
          ]}
          rows={data.signups}
          empty="No trial signups."
        />
      )}
    </AdminLayout>
  );
}
